/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';

import { LoopAttachmentButton, LoopAttachmentList } from '../pages/loops/LoopAttachments';
import type { LoopAttachmentsState } from '../pages/loops/LoopAttachments';
import { RecordingToggle, useLoopCreationOptions } from '../pages/loops/LoopCreationOptions';
import { LoopWatermarkToggle } from '../pages/loops/LoopWatermarkToggle';
import type { LoopLaunchOptions } from '../services/landingSkillRouting';
import { loopService } from '../services/loopService';
import type { BrowserProfile } from '../services/loopService';

import { BrowserProfilePicker } from './BrowserProfilePicker';
import { ProjectVisibilityDropdown } from './ProjectVisibilityDropdown';

import './LandingLoopControls.css';

const useLandingProfiles = (authenticated: boolean) => {
  const [profiles, setProfiles] = useState<BrowserProfile[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!authenticated) return undefined;
    const controller = new globalThis.AbortController();
    let pending = false;
    const refresh = async () => {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      try {
        const result = await loopService.profiles({ signal: controller.signal });

        if (!controller.signal.aborted) { setProfiles(result.filter((profile) => profile.status !== 'revoked')); setError(false); }
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally { pending = false; }
    };

    void refresh();
    const timer = window.setInterval(() => void refresh(), 15000);
    const onFocus = () => void refresh();

    window.addEventListener('focus', onFocus);

    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [authenticated]);

  return { profiles, error };
};

/* Same contract as the composer's landing visibility control: local state plus an event
   the landing page listens for. */
const LandingVisibilityControl: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const [isPublic, setIsPublic] = useState(true);

  return (
    <ProjectVisibilityDropdown
      projectId={null}
      isPublic={isPublic}
      disabled={disabled}
      onVisibilityChange={(_projectId, next) => {
        setIsPublic(next);
        window.dispatchEvent(new globalThis.CustomEvent('landing_visibility_changed', { detail: { isPublic: next } }));
      }}
    />
  );
};

const GearIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8.92 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09A1.65 1.65 0 0 0 19.4 15z" />
  </svg>
);

export const LandingLoopControls: React.FC<{
  authenticated: boolean; disabled?: boolean; onChange: (options: LoopLaunchOptions) => void;
  attachments?: LoopAttachmentsState; onSignIn?: () => void;
}> = ({ authenticated, disabled, onChange, attachments, onSignIn }) => {
  const { profiles, error } = useLandingProfiles(authenticated);
  const { profileId, setProfileId, recordVideo, setRecordVideo,
    watermarkEnabled, setWatermarkEnabled } = useLoopCreationOptions(profiles);
  const attachmentKey = attachments?.attachmentIds.join(',') || '';
  const notice = !authenticated ? 'Sign in to choose a connected browser.'
    : error ? 'Browser profiles could not be loaded. Checking again shortly.' : '';

  useEffect(() => { onChange({ browserProfileId: profileId || undefined, recordVideo, watermarkEnabled,
    attachmentIds: attachmentKey ? attachmentKey.split(',') : [] }); },
  [onChange, profileId, recordVideo, watermarkEnabled, attachmentKey]);

  // Phones: the toolbar keeps the paperclip and model picker, and this button expands the box
  // with a row holding the other Loop settings. Desktop hides the button and shows the same
  // controls inline, as before (CSS switches the layout; the controls are rendered once).
  const [settingsOpen, setSettingsOpen] = useState(false);

  return <div className="landing-loop-controls">
    {/* The paperclip stays in the toolbar on phones too; only the settings below fold away. */}
    {attachments && <LoopAttachmentButton state={attachments} disabled={disabled}
      onSignIn={!authenticated ? onSignIn : undefined} />}
    <button type="button" className="action-btn landing-loop-settings__toggle" aria-label="Loop settings"
      title="Loop settings" aria-expanded={settingsOpen} aria-controls="landing-loop-settings"
      onClick={() => setSettingsOpen((open) => !open)}>
      <GearIcon />
    </button>
    <div id="landing-loop-settings" className={`landing-loop-settings${settingsOpen ? ' is-open' : ''}`}>
      <BrowserProfilePicker profiles={profiles} value={profileId} onChange={setProfileId}
        disabled={disabled || !authenticated} compact />
      <div className="loop-recording-controls">
        <RecordingToggle value={recordVideo} onChange={setRecordVideo} disabled={disabled} compact />
        <LoopWatermarkToggle value={watermarkEnabled} onChange={setWatermarkEnabled} disabled={disabled} />
      </div>
      {/* Desktop keeps the composer's own globe next to the model; phones show this one here. */}
      <span className="landing-loop-settings__visibility"><LandingVisibilityControl disabled={disabled} /></span>
      {notice && <span className="landing-loop-controls__notice" role="status" title={notice} aria-label={notice}>
        <i className="fa-solid fa-circle-info" aria-hidden="true" /></span>}
    </div>
    {attachments && <LoopAttachmentList state={attachments} disabled={disabled} />}
  </div>;
};
